<template>
  <div class="locations-container">
    <div class="component-header">
      <h2>Location Management</h2>
      <button @click="goBack" class="back-btn">Back to Home</button>
    </div>
    
    <div class="locations-content">
      <!-- Left panel for location list -->
      <div class="locations-list">
        <div class="list-header">
          <h3>Locations</h3>
          <div class="view-toggles">
            <button 
              @click="showCollections = false" 
              :class="{ 'active': !showCollections }"
              class="toggle-btn"
            >Individual</button>
            <button 
              @click="showCollections = true" 
              :class="{ 'active': showCollections }"
              class="toggle-btn"
            >Collections</button>
          </div>
          <div v-if="!showCollections" class="filter-buttons">
            <button 
              @click="filterLocations('all')" 
              :class="{ 'active': currentFilter === 'all' }"
            >All</button>
            <button 
              @click="filterLocations('depot')" 
              :class="{ 'active': currentFilter === 'depot' }"
            >Depots</button>
            <button 
              @click="filterLocations('demand')" 
              :class="{ 'active': currentFilter === 'demand' }"
            >Demands</button>
          </div>
          <div class="action-buttons">
            <button v-if="showCollections" @click="showUploadModal = true" class="add-btn">+ Upload CSV</button>
            <button v-if="!showCollections" @click="showNewLocationForm" class="add-btn">+ Add Location</button>
          </div>
        </div>
        
        <!-- Individual Locations View -->
        <div v-if="!showCollections" class="locations-scroll-container">
          <div v-if="isLoading" class="loading">Loading locations...</div>
          
          <div v-else-if="filteredLocations.length === 0" class="empty-state">
            No locations found. Add a new location to get started.
          </div>
          
          <ul v-else class="location-items">
            <li 
              v-for="location in filteredLocations" 
              :key="location.id" 
              :class="{ 
                'selected': selectedLocation && selectedLocation.id === location.id, 
                'map-selected': isSelectedFromMap(location),
                'depot': location.type === 'depot', 
                'demand': location.type === 'demand' 
              }"
              @click="selectLocation(location)"
            >
              <div class="location-item">
                <div class="location-info">
                  <span class="location-name">
                    {{ location.name }}
                    <span v-if="isSelectedFromMap(location)" class="selection-indicator">📍</span>
                  </span>
                  <span class="location-type">{{ location.type === 'depot' ? 'Depot' : 'Demand Point' }}</span>
                  <span v-if="location.collectionId" class="collection-badge">Collection</span>
                  <span v-if="isSelectedFromMap(location)" class="map-selected-badge">Map Selected</span>
                </div>
                <div class="location-coords">
                  {{ formatCoordinates(location.coordinates) }}
                </div>
              </div>
            </li>
          </ul>
        </div>
        
        <!-- Collections View -->
        <div v-else>
          <div v-if="isLoading" class="loading">Loading collections...</div>
          
          <div v-else-if="collections.length === 0" class="empty-state">
            No collections found. Upload a CSV file to create your first collection.
          </div>
          
          <div v-else class="collections-list">
            <div 
              v-for="collection in collections" 
              :key="collection.id"
              class="collection-item"
              :class="{ 'expanded': selectedCollection && selectedCollection.id === collection.id }"
            >
              <div class="collection-header" @click="toggleCollection(collection)">
                <div class="collection-info">
                  <span class="collection-name">{{ collection.name }}</span>
                  <span class="collection-count">{{ collection.location_count }} locations</span>
                </div>
                <div class="collection-actions">
                  <button @click.stop="downloadCollection(collection)" class="download-collection-btn" title="Download as CSV">📥</button>
                  <button @click.stop="deleteCollection(collection)" class="delete-collection-btn" title="Delete collection">🗑️</button>
                  <span class="expand-icon">{{ selectedCollection && selectedCollection.id === collection.id ? '▼' : '▶' }}</span>
                </div>
              </div>
              
              <div v-if="selectedCollection && selectedCollection.id === collection.id" class="collection-locations">
                <div v-if="collectionLocationsLoading" class="loading">Loading locations...</div>
                <ul v-else class="location-items">
                  <li 
                    v-for="location in collectionLocations" 
                    :key="location.id" 
                    :class="{ 'selected': selectedLocation && selectedLocation.id === location.id, 'depot': location.type === 'depot', 'demand': location.type === 'demand' }"
                    @click="selectLocation(location)"
                  >
                    <div class="location-item collection-location">
                      <div class="location-info">
                        <span class="location-name">{{ location.name }}</span>
                        <span class="location-type">{{ location.type === 'depot' ? 'Depot' : 'Demand Point' }}</span>
                      </div>
                      <div class="location-coords">
                        {{ formatCoordinates(location.coordinates) }}
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Right panel for location details/form -->
      <div class="location-details">
        <template v-if="isEditing || isCreating">
          <h3>{{ isCreating ? 'Add New Location' : 'Edit Location' }}</h3>
          
          <form @submit.prevent="saveLocation" class="location-form">
            <div class="form-group">
              <label for="name">Location Name*</label>
              <input 
                type="text" 
                id="name" 
                v-model="locationForm.name" 
                placeholder="e.g., Main Warehouse" 
                required
              />
            </div>
            
            <div class="form-group">
              <label for="type">Location Type*</label>
              <select id="type" v-model="locationForm.type" required @change="handleTypeChange">
                <option value="depot">Depot</option>
                <option value="demand">Demand Point</option>
              </select>
            </div>
            
            <div class="form-group">
              <label>Select Location on Map*</label>
              <div id="mini-map" ref="miniMap" class="mini-map">
                <span v-if="!miniMap">Loading map...</span>
              </div>
              
              <div class="manual-coordinates">
                <label>Or Enter Coordinates Manually:</label>
                <div class="coordinates-row">
                  <div class="coordinate-input">
                    <label for="longitude">Longitude:</label>
                    <input 
                      type="text" 
                      id="longitude"
                      placeholder="e.g. 29.0335" 
                      v-model="locationForm.manualLng"
                    />
                  </div>
                  
                  <div class="coordinate-input">
                    <label for="latitude">Latitude:</label>
                    <input 
                      type="text" 
                      id="latitude"
                      placeholder="e.g. 41.0082" 
                      v-model="locationForm.manualLat"
                    />
                  </div>
                  
                  <button @click="applyManualCoordinates" class="apply-btn">Apply</button>
                </div>
              </div>
              
              <div class="coordinates-display">
                {{ formatCoordinates(locationForm.coordinates) || 'No coordinates set yet' }}
              </div>
            </div>
            
            <!-- Depot-specific fields -->
            <template v-if="locationForm.type === 'depot'">
              <div class="form-group">
                <label for="isActive">Status</label>
                <div class="toggle-switch">
                  <input 
                    type="checkbox" 
                    id="isActive" 
                    v-model="locationForm.isActive"
                  />
                  <label for="isActive" class="switch-label">{{ locationForm.isActive ? 'Active' : 'Inactive' }}</label>
                </div>
              </div>                <div class="form-group">
                <label for="assignedVehicles">Assigned Vehicles</label>
                <div class="instruction-text">Hold Ctrl (Windows) or Cmd (Mac) key while clicking to select multiple vehicles</div>
                <div class="select-wrapper">
                  <select 
                    id="assignedVehicles" 
                    v-model="locationForm.assignedVehicles" 
                    multiple
                    size="6"
                    class="multi-select"
                  >
                    <option v-for="vehicle in vehicles" :key="vehicle.id" :value="vehicle.id">
                      {{ vehicle.plate }} - {{ vehicle.type }}
                    </option>
                  </select>
                  <div class="selected-count" v-if="locationForm.assignedVehicles && locationForm.assignedVehicles.length">
                    {{ locationForm.assignedVehicles.length }} vehicle(s) selected
                  </div>
                  <div class="selected-count" v-else>
                    No vehicles selected
                  </div>
                </div>
              </div>
            </template>
            
            <!-- Demand-specific fields -->
            <template v-if="locationForm.type === 'demand'">
              <div class="form-row">
                <div class="form-group half">
                  <label for="timeWindowStart">Time Window Start</label>
                  <input 
                    type="time" 
                    id="timeWindowStart" 
                    v-model="locationForm.timeWindowStart"
                  />
                </div>
                
                <div class="form-group half">
                  <label for="timeWindowEnd">Time Window End</label>
                  <input 
                    type="time" 
                    id="timeWindowEnd" 
                    v-model="locationForm.timeWindowEnd"
                  />
                </div>
              </div>
              
              <div class="form-group">
                <label for="vehicleCapability">Required Vehicle Capability</label>
                <select id="vehicleCapability" v-model="locationForm.vehicleCapability">
                  <option value="">Any</option>
                  <option value="Mini">Mini</option>
                  <option value="Midi">Midi</option>
                  <option value="Maxi">Maxi</option>
                </select>
              </div>
              
              <div class="form-group">
                <label for="assignedDepot">Assigned Depot</label>
                <select id="assignedDepot" v-model="locationForm.assignedDepot">
                  <option value="">None</option>
                  <option v-for="depot in depots" :key="depot.id" :value="depot.id">
                    {{ depot.name }}
                  </option>
                </select>
              </div>
              
              <div class="form-group">
                <label for="demand">Demand Quantity</label>
                <input 
                  type="number" 
                  id="demand" 
                  v-model.number="locationForm.demand" 
                  min="0" 
                  step="1"
                />
              </div>
            </template>
            
            <div class="form-buttons">
              <button type="button" @click="cancelEdit" class="cancel-btn">Cancel</button>
              <button type="submit" class="save-btn">{{ isCreating ? 'Create Location' : 'Update Location' }}</button>
            </div>
          </form>
        </template>
        
        <template v-else-if="selectedLocation">
          <div class="location-view">
            <div class="view-header">
              <h3>{{ selectedLocation.name }} ({{ selectedLocation.type === 'depot' ? 'Depot' : 'Demand Point' }})</h3>
              <div class="action-buttons">
                <button @click="editLocation" class="edit-btn">Edit</button>
                <button @click="confirmDelete" class="delete-btn">Delete</button>
              </div>
            </div>
            
            <div class="location-map-view">
              <div id="detail-map" ref="detailMap" class="detail-map"></div>
            </div>
            
            <div class="location-details-content">
              <div class="detail-item">
                <span class="detail-label">Coordinates:</span>
                <span class="detail-value">{{ formatCoordinates(selectedLocation.coordinates) }}</span>
              </div>
              
              <!-- Depot-specific details -->
              <template v-if="selectedLocation.type === 'depot'">
                <div class="detail-item">
                  <span class="detail-label">Status:</span>
                  <span class="detail-value" :class="selectedLocation.isActive ? 'active-status' : 'inactive-status'">
                    {{ selectedLocation.isActive ? 'Active' : 'Inactive' }}
                  </span>
                </div>
                
                <div class="detail-item">
                  <span class="detail-label">Assigned Vehicles:</span>
                  <span class="detail-value" v-if="selectedLocation.assignedVehicles && selectedLocation.assignedVehicles.length">
                    {{ getAssignedVehiclesNames() }}
                  </span>
                  <span class="detail-value" v-else>No vehicles assigned</span>
                </div>
              </template>
              
              <!-- Demand-specific details -->
              <template v-if="selectedLocation.type === 'demand'">
                <div class="detail-item" v-if="selectedLocation.timeWindowStart && selectedLocation.timeWindowEnd">
                  <span class="detail-label">Time Window:</span>
                  <span class="detail-value">{{ selectedLocation.timeWindowStart }} - {{ selectedLocation.timeWindowEnd }}</span>
                </div>
                
                <div class="detail-item" v-if="selectedLocation.vehicleCapability">
                  <span class="detail-label">Required Vehicle:</span>
                  <span class="detail-value">{{ selectedLocation.vehicleCapability }}</span>
                </div>
                
                <div class="detail-item" v-if="selectedLocation.assignedDepot">
                  <span class="detail-label">Assigned Depot:</span>
                  <span class="detail-value">{{ getAssignedDepotName() }}</span>
                </div>
                
                <div class="detail-item" v-if="selectedLocation.demand !== undefined">
                  <span class="detail-label">Demand Quantity:</span>
                  <span class="detail-value">{{ selectedLocation.demand }}</span>
                </div>
              </template>
            </div>
          </div>
        </template>
        
        <template v-else>
          <div class="empty-details">
            <p>Select a location from the list or add a new one</p>
          </div>
        </template>
      </div>
    </div>
    
    <!-- Confirmation Dialog -->
    <div v-if="showDeleteConfirm" class="delete-confirmation-dialog">
      <div class="dialog-content">
        <h4>Confirm Deletion</h4>
        <p>Are you sure you want to delete location <strong>{{ selectedLocation.name }}</strong>?</p>
        <p class="warning">This action cannot be undone.</p>
        <div class="dialog-buttons">
          <button @click="showDeleteConfirm = false" class="cancel-btn">Cancel</button>
          <button @click="deleteLocation" class="confirm-delete-btn">Delete</button>
        </div>
      </div>
    </div>
    
    <!-- Upload Modal -->
    <LocationsUploadModal 
      v-if="showUploadModal" 
      @close="showUploadModal = false" 
      @collection-uploaded="handleCollectionUploaded"
    />
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import LocationsService from '../services/LocationsService';
import VehiclesService from '../services/VehiclesService';
import LocationsUploadModal from './LocationsUploadModal.vue';

// Explicitly check if mapboxgl is loaded
if (!window.mapboxgl && mapboxgl) {
  window.mapboxgl = mapboxgl;
  console.log('Explicitly set mapboxgl on window');
}

export default {
  name: 'LocationsComponent',
  props: {
    selectedLocationsFromMap: {
      type: Array,
      default: () => []
    }
  },
  components: {
    LocationsUploadModal
  },
  
  data() {
    return {
      locations: [],
      vehicles: [],
      depots: [],
      collections: [],
      selectedLocation: null,
      selectedCollection: null,
      isLoading: true,
      isEditing: false,
      isCreating: false,
      showDeleteConfirm: false,
      showUploadModal: false,
      currentFilter: 'all',
      showCollections: false,
      collectionLocations: [],
      collectionLocationsLoading: false,
      miniMap: null,
      detailMap: null,
      locationMarker: null,
      accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
      mapLoadError: false,
      locationForm: {
        name: '',
        type: 'depot',
        coordinates: null,
        isActive: true,
        assignedVehicles: [], // Ensure this is initialized as an array
        // Demand specific fields
        timeWindowStart: '',
        timeWindowEnd: '',
        vehicleCapability: '',
        assignedDepot: '',
        demand: 0,
        // Manual coordinate fields
        manualLng: '',
        manualLat: ''
      }
    };
  },
  
  computed: {
    filteredLocations() {
      let filtered = this.locations;
      
      // Filter by type if not showing all
      if (this.currentFilter !== 'all') {
        filtered = filtered.filter(location => location.type === this.currentFilter);
      }
      
      // When showing individual view, only show locations not in collections
      if (!this.showCollections) {
        filtered = filtered.filter(location => !location.collectionId);
      }
      
      // Prioritize selected locations from map at the top
      if (this.selectedLocationsFromMap && this.selectedLocationsFromMap.length > 0) {
        const selectedIds = this.selectedLocationsFromMap.map(loc => loc.id || loc._id);
        const selected = filtered.filter(loc => selectedIds.includes(loc.id || loc._id));
        const notSelected = filtered.filter(loc => !selectedIds.includes(loc.id || loc._id));
        
        return [...selected, ...notSelected];
      }
      
      return filtered;
    },
    
    selectedLocationCount() {
      return this.selectedLocationsFromMap ? this.selectedLocationsFromMap.length : 0;
    }
  },
  
  mounted() {
    // Set the Mapbox access token
    mapboxgl.accessToken = this.accessToken;
    
    
    // Ensure CSS is loaded
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://api.mapbox.com/mapbox-gl-js/v2.14.1/mapbox-gl.css';
    document.head.appendChild(link);
    
    // Load data
    this.loadLocations();
    this.loadVehicles();
    this.loadCollections();
    
    // Check if we need to navigate to a specific location
    this.handleRouteParams();
    
    // Add window resize listener to handle Mapbox reflows
    window.addEventListener('resize', this.handleResize);
  },
  
  beforeUnmount() {
    // Remove event listeners and clean up maps
    window.removeEventListener('resize', this.handleResize);
    
    if (this.miniMap) {
      this.miniMap.remove();
      this.miniMap = null;
    }
    
    if (this.detailMap) {
      this.detailMap.remove();
      this.detailMap = null;
    }
  },
  
  methods: {
    /**
     * Check if a location is selected from the map
     */
    isSelectedFromMap(location) {
      if (!this.selectedLocationsFromMap || this.selectedLocationsFromMap.length === 0) {
        return false;
      }
      
      const locationId = location.id || location._id;
      return this.selectedLocationsFromMap.some(selected => (selected.id || selected._id) === locationId);
    },
    initMiniMap() {
      console.log('Initializing mini map');
      
      // Force a timeout to ensure DOM is fully updated
      setTimeout(() => {
        // Safety check to make sure the element exists
        if (!this.$refs.miniMap) {
          console.error('Mini map container not found in DOM');
          return;
        }
        
        // Set explicit height to ensure container is visible
        this.$refs.miniMap.style.height = '300px';
        this.$refs.miniMap.style.width = '100%';
        
        // Remove existing map if any
        if (this.miniMap) {
          this.miniMap.remove();
          this.miniMap = null;
        }
        
        try {
          mapboxgl.accessToken = this.accessToken;
          // Create the mini map
          this.miniMap = new mapboxgl.Map({
            container: this.$refs.miniMap,
            style: 'mapbox://styles/mapbox/streets-v11',
            center: [29.0335, 41.0082],
            zoom: 10,
            attributionControl: false
          });
          // Add navigation controls
          this.miniMap.addControl(new mapboxgl.NavigationControl());
          
          // After map load, attach click handler and resize for pixel-accurate clicks
          this.miniMap.on('load', () => {
            // Ensure proper internal sizing
            this.miniMap.resize();
            
            // Click listener: place marker at exact click location
            this.miniMap.on('click', (e) => {
              const lngLat = e.lngLat;
              const coords = [lngLat.lng, lngLat.lat];
              console.log('MiniMap click at:', coords);
              
              // Update form values
              this.locationForm.coordinates = coords;
              this.locationForm.manualLng = lngLat.lng.toString();
              this.locationForm.manualLat = lngLat.lat.toString();
              
              // Place or move marker
              this.setLocationMarker(coords);
            });
            
            // If editing or loaded with coords, show existing marker
            if (this.locationForm.coordinates) {
              this.setLocationMarker(this.locationForm.coordinates);
            }
          });
        } catch (error) {
          console.error('Error initializing mini map:', error);
        }
      }, 200);
    },
    
    initDetailMap() {
      if (!this.selectedLocation || !this.selectedLocation.coordinates) return;
      
      // Wait for DOM update
      this.$nextTick(() => {
        if (!this.$refs.detailMap) return;
        
        // Clean up existing map
        if (this.detailMap) {
          this.detailMap.remove();
          this.detailMap = null;
        }
        
        try {
          // Ensure token is set
          mapboxgl.accessToken = this.accessToken;
          
          // Create detail map
          this.detailMap = new mapboxgl.Map({
            container: this.$refs.detailMap,
            style: 'mapbox://styles/mapbox/streets-v11',
            center: this.selectedLocation.coordinates,
            zoom: 14,
            attributionControl: false
          });
          
          // Add navigation controls
          this.detailMap.addControl(new mapboxgl.NavigationControl());          // Add marker for location
          this.detailMap.on('load', () => {
            // Create custom marker element for precision placement
            const markerElement = document.createElement('div');
            markerElement.className = 'custom-detail-marker';
            markerElement.style.width = '20px';
            markerElement.style.height = '20px';
            markerElement.style.borderRadius = '50%';
            markerElement.style.backgroundColor = this.selectedLocation.type === 'depot' ? '#3498db' : '#e74c3c';
            markerElement.style.border = '2px solid white';
            
            // Create marker with exact coordinates
            new mapboxgl.Marker({
              element: markerElement,
              anchor: 'center', // Ensure marker is placed exactly at the coordinates
              offset: [0, 0] // No offset
            })
              .setLngLat(this.selectedLocation.coordinates)
              .addTo(this.detailMap);
              
            console.log('Detail marker placed at exact coordinates:', this.selectedLocation.coordinates);
          });
        } catch (error) {
          console.error('Error initializing detail map:', error);
        }
      });
    },
  setLocationMarker(coords) {
      console.log('Setting marker at EXACT coordinates without any modification:', coords);
      
      // Preserve the exact coordinates without any conversion or modification
      const exactLng = coords[0]; 
      const exactLat = coords[1];
      
      // Safety check - ensure miniMap exists
      if (!this.miniMap) {
        console.error("Cannot set marker: map not initialized");
        // Store coordinates anyway - using exact values
        this.locationForm.coordinates = [exactLng, exactLat];
        return;
      }
      
      // Remove existing marker if any
      if (this.locationMarker) {
        this.locationMarker.remove();
        this.locationMarker = null;
      }
      
      try {
        // Create a new marker at the exact coordinates without any modification
        this.locationMarker = new mapboxgl.Marker({
          color: this.locationForm.type === 'depot' ? '#3498db' : '#e74c3c',
          draggable: true,
          anchor: 'center' // Center anchor to place exactly at coordinates
        })
          .setLngLat([exactLng, exactLat])
          .addTo(this.miniMap);
        
        // [Removed auto-centering to preserve marker position visibility]
        // (If desired, you can re-enable centering with panTo instead of flyTo)
        
        // Update coordinates when marker is dragged - preserve exact values
        this.locationMarker.on('dragend', () => {
          const lngLat = this.locationMarker.getLngLat();
          const updatedCoords = [lngLat.lng, lngLat.lat];
          this.locationForm.coordinates = updatedCoords;
          this.locationForm.manualLng = lngLat.lng.toString();
          this.locationForm.manualLat = lngLat.lat.toString();
          console.log('Marker dragged to exact coordinates:', updatedCoords);
        });
      } catch (error) {
        console.error('Error creating marker:', error);
      }
    },loadLocations() {
      this.isLoading = true;
      console.log('Loading locations from database...');
      
      LocationsService.getLocations()
        .then(response => {
          console.log('Locations loaded:', response.data);
          this.locations = response.data || [];
          
          // Extract depots for dropdown selection - use separate API call to ensure all depots are loaded
          LocationsService.getDepots()
            .then(depotResponse => {
              console.log('Depots loaded separately:', depotResponse.data);
              this.depots = depotResponse.data || [];
              
              // Sort depots alphabetically by name
              this.depots.sort((a, b) => a.name.localeCompare(b.name));
            })
            .catch(depotError => {
              console.error('Error fetching depots separately:', depotError);
              // Fallback to filtering from locations
              this.depots = this.locations.filter(loc => loc.type === 'depot');
              console.log('Extracted depots from locations as fallback:', this.depots);
            });
          
          // Sort locations alphabetically by name for better display
          this.locations.sort((a, b) => a.name.localeCompare(b.name));
          
          this.isLoading = false;
          
          // Check route parameters after locations are loaded
          this.handleRouteParams();
        })
        .catch(error => {
          console.error('Error fetching locations:', error);
          alert('Failed to load locations from database. Please check the server connection.');
          this.isLoading = false;
        });
    },
      loadVehicles() {
      console.log('Loading vehicles from database...');
      
      VehiclesService.getVehicles()
        .then(response => {
          console.log('Vehicles loaded:', response.data);
          this.vehicles = response.data || [];
          
          // Sort vehicles by plate number for better display
          this.vehicles.sort((a, b) => a.plate.localeCompare(b.plate));
        })
        .catch(error => {
          console.error('Error fetching vehicles:', error);
          alert('Failed to load vehicles from database. Multi-select might not work properly.');
        });
    },
    
    selectLocation(location) {
      if (this.isEditing || this.isCreating) {
        return; // Don't allow selection while editing or creating
      }
      
      this.selectedLocation = location;
      this.$nextTick(() => {
        if (this.$refs.detailMap) {
          this.initDetailMap();
        }
      });
    },
      showNewLocationForm() {
      this.isCreating = true;
      this.isEditing = false;
      this.selectedLocation = null;
      
      // Reset form with explicit empty array for assignedVehicles
      this.locationForm = {
        name: '',
        type: 'depot',
        coordinates: null,
        isActive: true,
        assignedVehicles: [], // Explicitly initialize as empty array
        timeWindowStart: '',
        timeWindowEnd: '',
        vehicleCapability: '',
        assignedDepot: '',
        demand: 0,
        manualLng: '',
        manualLat: ''
      };
      
      console.log('Form reset for new location, assignedVehicles:', this.locationForm.assignedVehicles);
      
      // Initialize map after DOM update
      this.$nextTick(() => {
        this.initMiniMap();
      });
    },
    
    editLocation() {
      if (!this.selectedLocation) return;
      
      this.isEditing = true;
      this.isCreating = false;
      
      // Copy selected location data to form
      this.locationForm = { ...this.selectedLocation };
      
      // Set manual coordinate fields
      if (this.locationForm.coordinates) {
        this.locationForm.manualLng = this.locationForm.coordinates[0].toString();
        this.locationForm.manualLat = this.locationForm.coordinates[1].toString();
      }
      
      // Initialize map after DOM update
      this.$nextTick(() => {
        this.initMiniMap();
      });
    },
    
    cancelEdit() {
      this.isEditing = false;
      this.isCreating = false;
      
      if (this.selectedLocation) {
        this.$nextTick(() => {
          this.initDetailMap();
        });
      }
    },
    
    handleTypeChange() {
      // Update marker color if exists
      if (this.locationMarker && this.locationForm.coordinates) {
        this.locationMarker.remove();
        this.setLocationMarker(this.locationForm.coordinates);
      }
    },    applyManualCoordinates(event) {
      event.preventDefault();
      
      // Parse using exact string values without rounding
      const lng = parseFloat(this.locationForm.manualLng);
      const lat = parseFloat(this.locationForm.manualLat);
      
      console.log('Applying manual coordinates with precision:', lng, lat);
      
      if (!isNaN(lng) && !isNaN(lat)) {
        // Check valid range
        if (lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90) {
          // Create coordinates array in the proper format [longitude, latitude]
          // Don't recalculate values, use the exact parsed values
          const coords = [lng, lat];
          this.locationForm.coordinates = coords;
          
          console.log('Setting exact coordinates to:', coords);
          
          // Update map marker if map exists
          if (this.miniMap) {
            // Ensure the map is fully loaded
            if (this.miniMap.loaded()) {
              // Remove any existing marker first
              if (this.locationMarker) {
                this.locationMarker.remove();
                this.locationMarker = null;
              }
              
              // Create marker directly without using setLocationMarker for precision
              const markerElement = document.createElement('div');
              markerElement.className = 'custom-marker';
              markerElement.style.width = '20px';
              markerElement.style.height = '20px';
              markerElement.style.borderRadius = '50%';
              markerElement.style.backgroundColor = this.locationForm.type === 'depot' ? '#3498db' : '#e74c3c';
              markerElement.style.border = '2px solid white';
              
              // Create a fresh marker with the exact coordinates
              this.locationMarker = new mapboxgl.Marker({
                element: markerElement,
                draggable: true,
                anchor: 'center',
                offset: [0, 0] // No offset
              })
                .setLngLat(coords)
                .addTo(this.miniMap);
              
              // Update coordinates when marker is dragged
              this.locationMarker.on('dragend', () => {
                const lngLat = this.locationMarker.getLngLat();
                const updatedCoords = [lngLat.lng, lngLat.lat];
                this.locationForm.coordinates = updatedCoords;
                this.locationForm.manualLng = lngLat.lng.toString();
                this.locationForm.manualLat = lngLat.lat.toString();
                console.log('Marker dragged to exact coordinates:', updatedCoords);
              });
              
              // Center the map on the coordinates
              this.miniMap.flyTo({
                center: coords,
                zoom: 13,
                duration: 0 // Immediate centering
              });
              
              console.log('Manual coordinate marker placed at:', this.locationMarker.getLngLat());
            } else {
              // Wait for map to load
              this.miniMap.once('load', () => {
                // Same marker creation code as above
                if (this.locationMarker) {
                  this.locationMarker.remove();
                  this.locationMarker = null;
                }
                
                const markerElement = document.createElement('div');
                markerElement.className = 'custom-marker';
                markerElement.style.width = '20px';
                markerElement.style.height = '20px';
                markerElement.style.borderRadius = '50%';
                markerElement.style.backgroundColor = this.locationForm.type === 'depot' ? '#3498db' : '#e74c3c';
                markerElement.style.border = '2px solid white';
                
                this.locationMarker = new mapboxgl.Marker({
                  element: markerElement,
                  draggable: true,
                  anchor: 'center',
                  offset: [0, 0]
                })
                  .setLngLat(coords)
                  .addTo(this.miniMap);
                
                this.locationMarker.on('dragend', () => {
                  const lngLat = this.locationMarker.getLngLat();
                  const updatedCoords = [lngLat.lng, lngLat.lat];
                  this.locationForm.coordinates = updatedCoords;
                  this.locationForm.manualLng = lngLat.lng.toString();
                  this.locationForm.manualLat = lngLat.lat.toString();
                });
                
                // Center the map on the coordinates
                this.miniMap.flyTo({
                  center: coords,
                  zoom: 13,
                  duration: 0
                });
              });
            }
          } else {
            console.log('Map not available, coordinates set without marker');
          }
        } else {
          alert('Please enter valid coordinates: Longitude (-180 to 180) and Latitude (-90 to 90)');
        }
      } else {
        alert('Please enter valid numeric values for coordinates');
      }
    },
      saveLocation() {
      // Validate coordinates
      if (!this.locationForm.coordinates) {
        // Try to get from manual inputs
        this.applyManualCoordinates(new Event('submit'));
        
        if (!this.locationForm.coordinates) {
          alert('Please select a location on the map or enter coordinates manually');
          return;
        }
      }
      
      // Create a clean data object to send (fix assignedDepot empty string to null)
      const formData = { ...this.locationForm };
      
      // Convert empty string to null for assignedDepot (to prevent database integer conversion error)
      if (formData.assignedDepot === '') {
        formData.assignedDepot = null;
      }
      
      console.log('Saving location with data:', formData);
      
      if (this.isCreating) {
        // Creating new location
        LocationsService.createLocation(formData)
          .then(response => {
            console.log('Location created successfully:', response.data);
            this.locations.push(response.data);
            if (response.data.type === 'depot') {
              this.depots.push(response.data);
            }
            this.selectedLocation = response.data;
            this.isCreating = false;
            
            this.$nextTick(() => {
              this.initDetailMap();
            });
          })
          .catch(error => {
            console.error('Error creating location:', error);
            alert('Failed to create location. Please try again.');
          });
      } else if (this.isEditing) {
        // Updating existing location
        LocationsService.updateLocation(formData.id, formData)
          .then(response => {
            console.log('Location updated successfully:', response.data);
            // Update location in list
            const index = this.locations.findIndex(v => v.id === formData.id);
            if (index !== -1) {
              this.locations.splice(index, 1, response.data);
            }
            
            // Update depots list if needed
            if (response.data.type === 'depot') {
              const depotIndex = this.depots.findIndex(d => d.id === response.data.id);
              if (depotIndex !== -1) {
                this.depots.splice(depotIndex, 1, response.data);
              } else {
                this.depots.push(response.data);
              }
            } else {
              // Remove from depots if changed from depot to demand
              this.depots = this.depots.filter(d => d.id !== response.data.id);
            }
            
            this.selectedLocation = response.data;
            this.isEditing = false;
            
            this.$nextTick(() => {
              this.initDetailMap();
            });
          })
          .catch(error => {
            console.error('Error updating location:', error);
            alert('Failed to update location. Please try again.');
          });
      }
    },
    
    confirmDelete() {
      this.showDeleteConfirm = true;
    },
    
    deleteLocation() {
      if (!this.selectedLocation) return;
      
      LocationsService.deleteLocation(this.selectedLocation.id)
        .then(() => {
          // Remove location from list
          this.locations = this.locations.filter(l => l.id !== this.selectedLocation.id);
          
          // Remove from depots if needed
          if (this.selectedLocation.type === 'depot') {
            this.depots = this.depots.filter(d => d.id !== this.selectedLocation.id);
          }
          
          this.selectedLocation = null;
          this.showDeleteConfirm = false;
          
          if (this.detailMap) {
            this.detailMap.remove();
            this.detailMap = null;
          }
        })
        .catch(error => {
          console.error('Error deleting location:', error);
          alert('Failed to delete location. Please try again.');
        });
    },
    
    filterLocations(filter) {
      this.currentFilter = filter;
    },
    
    formatCoordinates(coords) {
      if (!coords) return '';
      return `[${coords[0].toFixed(4)}, ${coords[1].toFixed(4)}]`;
    },
    
    getAssignedVehiclesNames() {
      if (!this.selectedLocation || !this.selectedLocation.assignedVehicles) return '';
      
      const assignedVehicles = this.vehicles.filter(v => 
        this.selectedLocation.assignedVehicles.includes(v.id)
      );
      
      return assignedVehicles.map(v => v.plate).join(', ');
    },
    
    /**
     * Handle route parameters to navigate to specific location
     */
    handleRouteParams() {
      const locationId = this.$route.params.id;
      if (locationId && this.locations.length > 0) {
        // Find the location by ID
        const location = this.locations.find(loc => 
          (loc.id && loc.id.toString() === locationId) || 
          (loc._id && loc._id.toString() === locationId)
        );
        
        if (location) {
          this.selectLocation(location);
        } else {
          console.warn(`Location with ID ${locationId} not found`);
        }
      }
    },
    
    getAssignedDepotName() {
      if (!this.selectedLocation || !this.selectedLocation.assignedDepot) return 'None';
      
      const depot = this.depots.find(d => d.id === this.selectedLocation.assignedDepot);
      return depot ? depot.name : 'Unknown';
    },
    
    handleResize() {
      // Resize maps when window size changes
      if (this.miniMap) {
        this.miniMap.resize();
      }
      
      if (this.detailMap) {
        this.detailMap.resize();
      }
    },
    
    goBack() {
      // Emit an event to tell the parent component to switch back to the main view
      this.$emit('close-locations');
    },
    
    loadCollections() {
      console.log('Loading location collections...');
      
      LocationsService.getCollections()
        .then(response => {
          console.log('Collections loaded:', response.data);
          this.collections = response.data || [];
          
          // Sort collections by creation date (newest first)
          this.collections.sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
        })
        .catch(error => {
          console.error('Error fetching collections:', error);
          // Don't show alert for collections as it's not critical
        });
    },
    
    toggleCollection(collection) {
      if (this.selectedCollection && this.selectedCollection.id === collection.id) {
        // Collapse if already selected
        this.selectedCollection = null;
        this.collectionLocations = [];
      } else {
        // Expand and load locations
        this.selectedCollection = collection;
        this.loadCollectionLocations(collection.id);
      }
    },
    
    loadCollectionLocations(collectionId) {
      this.collectionLocationsLoading = true;
      
      LocationsService.getCollectionLocations(collectionId)
        .then(response => {
          console.log('Collection locations loaded:', response.data);
          this.collectionLocations = response.data || [];
          
          // Sort locations alphabetically
          this.collectionLocations.sort((a, b) => a.name.localeCompare(b.name));
          
          this.collectionLocationsLoading = false;
        })
        .catch(error => {
          console.error('Error fetching collection locations:', error);
          this.collectionLocationsLoading = false;
        });
    },
    
    downloadCollection(collection) {
      console.log('Downloading collection:', collection.name);
      
      // Get the collection locations
      LocationsService.getCollectionLocations(collection.id)
        .then(response => {
          const locations = response.data || [];
          
          if (locations.length === 0) {
            alert('No locations found in this collection to download.');
            return;
          }
          
          // Convert locations to CSV format
          const csvContent = this.convertToCSV(locations);
          
          // Create and download the file
          this.downloadCSV(csvContent, `${collection.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_locations.csv`);
        })
        .catch(error => {
          console.error('Error downloading collection:', error);
          alert('Failed to download collection. Please try again.');
        });
    },
    
    convertToCSV(locations) {
      if (locations.length === 0) return '';
      
      // Define CSV headers
      const headers = [
        'Name',
        'Type',
        'Longitude',
        'Latitude',
        'Is Active',
        'Demand',
        'Time Window Start',
        'Time Window End',
        'Vehicle Capability',
        'Assigned Depot',
        'Assigned Vehicles'
      ];
      
      // Create CSV rows
      const rows = locations.map(location => {
        const coords = location.coordinates || [0, 0];
        const assignedVehicles = location.assignedVehicles 
          ? (Array.isArray(location.assignedVehicles) ? location.assignedVehicles.join(';') : location.assignedVehicles)
          : '';
        
        return [
          `"${(location.name || '').replace(/"/g, '""')}"`,
          location.type || '',
          coords[0] || 0,
          coords[1] || 0,
          location.isActive ? 'TRUE' : 'FALSE',
          location.demand || '',
          location.timeWindowStart || '',
          location.timeWindowEnd || '',
          location.vehicleCapability || '',
          location.assignedDepot || '',
          `"${assignedVehicles.replace(/"/g, '""')}"`
        ].join(',');
      });
      
      // Combine headers and rows
      return [headers.join(','), ...rows].join('\n');
    },
    
    downloadCSV(csvContent, filename) {
      // Create a Blob with the CSV content
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create a download link
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // Fallback for browsers that don't support download attribute
        alert('Download not supported in this browser. Please use a modern browser.');
      }
    },
    
    deleteCollection(collection) {
      if (confirm(`Are you sure you want to delete the collection "${collection.name}" and all its locations?`)) {
        LocationsService.deleteCollection(collection.id, true)
          .then(() => {
            // Remove from collections list
            this.collections = this.collections.filter(c => c.id !== collection.id);
            
            // Clear selection if this was selected
            if (this.selectedCollection && this.selectedCollection.id === collection.id) {
              this.selectedCollection = null;
              this.collectionLocations = [];
            }
            
            // Reload individual locations to remove deleted ones
            this.loadLocations();
          })
          .catch(error => {
            console.error('Error deleting collection:', error);
            alert('Failed to delete collection. Please try again.');
          });
      }
    },
    
    handleCollectionUploaded(uploadResult) {
      console.log('Collection uploaded:', uploadResult);
      
      // Reload collections and locations
      this.loadCollections();
      this.loadLocations();
      
      // Show success message
      alert(`Collection "${uploadResult.name}" uploaded successfully with ${uploadResult.locations_created} locations!`);
    }
  }
};
</script>

<style scoped>
.locations-container {
  position: relative;
  width: 100%;
  height: 100vh;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
  overflow: hidden;
}

.component-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-shrink: 0; /* Prevent header from shrinking */
}

h2 {
  margin-top: 0;
  margin-bottom: 0;
  color: #2c3e50;
  font-size: 1.8rem;
}

.back-btn {
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.back-btn:hover {
  background-color: #5a6268;
}

.locations-content {
  display: flex;
  flex: 1;
  gap: 20px;
  min-height: 0;
  height: calc(100vh - 120px); /* Set explicit height */
  overflow: hidden;
}

/* Left panel - Location list */
.locations-list {
  flex: 0 0 300px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: calc(100vh - 140px); /* Limit height to viewport minus header */
  overflow: hidden;
}

.list-header {
  padding: 15px;
  border-bottom: 1px solid #e5e5e5;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.filter-buttons {
  display: flex;
  gap: 5px;
}

.filter-buttons button {
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 0.8rem;
  cursor: pointer;
}

.filter-buttons button.active {
  background-color: #3498db;
  color: white;
  border-color: #3498db;
}

.list-header h3 {
  margin: 0;
  color: #2c3e50;
}

.view-toggles {
  display: flex;
  gap: 5px;
  margin-bottom: 10px;
}

.toggle-btn {
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 0.8rem;
  cursor: pointer;
}

.toggle-btn.active {
  background-color: #3498db;
  color: white;
  border-color: #3498db;
}

.action-buttons {
  margin-top: 10px;
}

.add-btn {
  background-color: #3498db;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.add-btn:hover {
  background-color: #2980b9;
}

.loading, .empty-state {
  padding: 20px;
  text-align: center;
  color: #6c757d;
}

.location-items {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0; /* Important for flex overflow */
  max-height: 100%;
}

.location-items li {
  padding: 15px;
  border-bottom: 1px solid #e5e5e5;
  cursor: pointer;
  transition: background-color 0.2s;
}

.location-items li:hover {
  background-color: #f0f7fc;
}

.location-items li.selected {
  background-color: #e0f0ff;
}

.location-items li.depot {
  border-left: 4px solid #3498db;
}

.location-items li.demand {
  border-left: 4px solid #e74c3c;
}

.location-items li.map-selected {
  background-color: #e8f5e8;
  border: 2px solid #2ecc71;
  animation: mapSelectedGlow 2s ease-in-out infinite alternate;
}

@keyframes mapSelectedGlow {
  0% { box-shadow: 0 0 5px rgba(46, 204, 113, 0.5); }
  100% { box-shadow: 0 0 15px rgba(46, 204, 113, 0.8); }
}

.location-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.location-name {
  font-weight: bold;
  color: #2c3e50;
}

.location-type {
  font-size: 0.8rem;
  padding: 2px 6px;
  border-radius: 10px;
  background-color: #f0f0f0;
}

.selection-indicator {
  margin-left: 5px;
  font-size: 0.9rem;
  animation: bounce 1s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-3px); }
  60% { transform: translateY(-1px); }
}

.map-selected-badge {
  background-color: #2ecc71;
  color: white;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 5px;
}

.collection-badge {
  background-color: #9b59b6;
  color: white;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 5px;
}

.location-coords {
  font-size: 0.8rem;
  color: #666;
}

/* Collections styles */
.collections-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  max-height: 100%;
}

.collection-item {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.collection-item.expanded {
  border-color: #3498db;
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.1);
}

.collection-header {
  padding: 15px;
  background-color: #f8f9fa;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s;
}

.collection-header:hover {
  background-color: #e9ecef;
}

.collection-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.collection-name {
  font-weight: bold;
  color: #2c3e50;
  font-size: 1rem;
}

.collection-count {
  font-size: 0.8rem;
  color: #6c757d;
}

.collection-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.download-collection-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 4px;
  border-radius: 4px;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.download-collection-btn:hover {
  opacity: 1;
  background-color: rgba(52, 152, 219, 0.1);
}

.delete-collection-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 4px;
  border-radius: 4px;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.delete-collection-btn:hover {
  opacity: 1;
  background-color: rgba(220, 53, 69, 0.1);
}

.expand-icon {
  color: #6c757d;
  font-size: 0.8rem;
  font-weight: bold;
}

.collection-locations {
  border-top: 1px solid #e5e5e5;
  background-color: white;
}

.collection-location {
  padding-left: 20px;
  border-left: 3px solid #3498db;
}

/* Right panel - Location details */
.location-details {
  flex: 1;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  padding: 20px;
  overflow-y: auto;
}

.location-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-width: 600px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.form-row {
  display: flex;
  gap: 15px;
}

.form-group.half {
  flex: 1;
}

label {
  font-weight: bold;
  color: #333;
}

input, select {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
}

select[multiple] {
  height: 150px;
}

.multi-select {
  width: 100%;
  background-color: #ffffff;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px;
  min-height: 150px;
  font-size: 0.95rem;
}

.multi-select option {
  padding: 8px;
  margin: 2px 0;
}

.multi-select option:checked {
  background-color: #3498db;
  color: white;
}

.select-wrapper {
  position: relative;
}

.selected-count {
  margin-top: 8px;
  font-size: 0.85rem;
  color: #3498db;
  font-weight: bold;
  padding: 3px;
  background-color: #f0f7fc;
  border-radius: 4px;
  display: inline-block;
}

.toggle-switch {
  display: flex;
  align-items: center;
}

.switch-label {
  margin-left: 10px;
  font-weight: normal;
}

.mini-map {
  position: relative;
  height: 300px;
  width: 100%;
  min-height: 300px;
  background-color: #f8f8f8;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 10px;
  overflow: hidden;
}

.coordinates-display {
  margin-top: 8px;
  font-size: 0.9rem;
  color: #666;
  font-family: monospace;
}

.form-buttons {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.cancel-btn {
  background-color: #f0f0f0;
  color: #333;
  border: 1px solid #ddd;
  padding: 10px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.save-btn {
  background-color: #2ecc71;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.save-btn:hover {
  background-color: #27ae60;
}

/* Location view */
.location-view {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.edit-btn {
  background-color: #3498db;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
}

.delete-btn {
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
}

.edit-btn:hover {
  background-color: #2980b9;
}

.delete-btn:hover {
  background-color: #c0392b;
}

.location-map-view {
  width: 100%;
  margin-bottom: 20px;
}

.detail-map {
  height: 300px;
  border-radius: 8px;
  width: 100%;
  border: 1px solid #ddd;
  overflow: hidden;
  position: relative;
}

.location-details-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.detail-item {
  display: flex;
  align-items: baseline;
}

.detail-label {
  font-weight: bold;
  width: 150px;
  color: #555;
}

.active-status {
  color: #2ecc71;
  font-weight: bold;
}

.inactive-status {
  color: #e74c3c;
  font-weight: bold;
}

/* Delete confirmation dialog */
.delete-confirmation-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.dialog-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
}

.dialog-content h4 {
  margin-top: 0;
  color: #e74c3c;
}

.warning {
  color: #e74c3c;
  font-style: italic;
}

.dialog-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.confirm-delete-btn {
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
}

.confirm-delete-btn:hover {
  background-color: #c0392b;
}

.empty-details {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #6c757d;
  text-align: center;
}

/* Scrollbar styling for better visibility */
.location-items::-webkit-scrollbar,
.collections-list::-webkit-scrollbar {
  width: 8px;
}

.location-items::-webkit-scrollbar-track,
.collections-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.location-items::-webkit-scrollbar-thumb,
.collections-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.location-items::-webkit-scrollbar-thumb:hover,
.collections-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Container for individual locations scrolling */
.locations-scroll-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* Manual coordinates */
.manual-coordinates {
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.manual-coordinates > label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
}

.coordinates-row {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

.coordinate-input {
  flex: 1;
}

.coordinate-input label {
  display: block;
  font-size: 0.85rem;
  margin-bottom: 4px;
}

.coordinate-input input {
  width: 100%;
}

.apply-btn {
  background-color: #3498db;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 4px;
  cursor: pointer;
  height: 38px;
}

.apply-btn:hover {
  background-color: #2980b9;
}

.instruction-text {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 8px;
  font-style: italic;
}
</style>
